import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  Divider,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Home,
  Business,
  LocationOn,
  Star
} from '@mui/icons-material';
import { addressAPI } from '../services/api';
import BottomNavigation from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

// Australian states and territories
const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
];

const Addresses = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    type: 'home',
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      
      // Debug: Check if user is authenticated
      const token = localStorage.getItem('token');
      console.log('Auth token present:', !!token);
      
      const response = await addressAPI.getAddresses();
      if (response.success) {
        setAddresses(response.data || []);
      } else {
        setError('Failed to load addresses');
      }
    } catch (err) {
      console.error('Error fetching addresses:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async () => {
    try {
      // Validate required fields (name and phone are optional)
      if (!formData.address || !formData.city || !formData.state || !formData.pincode) {
        setError('Please fill in address, city, state, and postcode');
        return;
      }

      // Validate postcode format (4 digits for Australia)
      if (!/^\d{4}$/.test(formData.pincode)) {
        setError('Postcode must be 4 digits');
        return;
      }

      // Map frontend fields to backend fields
      const addressData = {
        type: formData.type,
        name: formData.name || '',
        phone: formData.phone || '',
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        isDefault: formData.isDefault
      };

      // Debug: Log the data being sent
      console.log('Sending address data:', addressData);

      const response = await addressAPI.addAddress(addressData);
      if (response.success) {
        setShowAddDialog(false);
        resetForm();
        fetchAddresses();
      } else {
        setError(response.error || 'Failed to add address');
      }
    } catch (err) {
      console.error('Error adding address:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to add address');
    }
  };

  const handleEditAddress = async () => {
    try {
      // Validate required fields (name and phone are optional)
      if (!formData.address || !formData.city || !formData.state || !formData.pincode) {
        setError('Please fill in address, city, state, and postcode');
        return;
      }

      // Validate postcode format (4 digits for Australia)
      if (!/^\d{4}$/.test(formData.pincode)) {
        setError('Postcode must be 4 digits');
        return;
      }

      // Map frontend fields to backend fields
      const addressData = {
        type: formData.type,
        name: formData.name || '',
        phone: formData.phone || '',
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        isDefault: formData.isDefault
      };

      // Debug: Log the data being sent
      console.log('Sending edit address data:', addressData);

      const response = await addressAPI.updateAddress(editingAddress._id, addressData);
      if (response.success) {
        setShowEditDialog(false);
        setEditingAddress(null);
        resetForm();
        fetchAddresses();
      } else {
        setError(response.error || 'Failed to update address');
      }
    } catch (err) {
      console.error('Error updating address:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to update address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        const response = await addressAPI.deleteAddress(addressId);
        if (response.success) {
          fetchAddresses();
        } else {
          setError(response.error || 'Failed to delete address');
        }
      } catch (err) {
        console.error('Error deleting address:', err);
        setError('Failed to delete address');
      }
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await addressAPI.setDefaultAddress(addressId);
      if (response.success) {
        fetchAddresses();
      } else {
        setError(response.error || 'Failed to set default address');
      }
    } catch (err) {
      console.error('Error setting default address:', err);
      setError('Failed to set default address');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'home',
      name: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false
    });
  };

  const openEditDialog = (address) => {
    setEditingAddress(address);
    setFormData({
      type: address.type || 'home',
      name: address.label || address.contactName || '',
      phone: address.contactPhone || '',
      address: address.street || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.zipCode || '',
      isDefault: address.isDefault || false
    });
    setShowEditDialog(true);
  };

  const getAddressIcon = (type) => {
    switch (type) {
      case 'home':
        return <Home />;
      case 'work':
        return <Business />;
      default:
        return <LocationOn />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 7 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#0A2A66' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            My Addresses
          </Typography>
          <Button
            color="inherit"
            startIcon={<Add />}
            onClick={() => setShowAddDialog(true)}
          >
            Add
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {addresses.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 4 }}>
            <CardContent>
              <LocationOn sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Addresses Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add your first address to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowAddDialog(true)}
                sx={{
                  background: 'linear-gradient(135deg, #0A2A66 0%, #1a3a76 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1a3a76 0%, #0A2A66 100%)',
                  }
                }}
              >
                Add Address
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {addresses.map((address) => (
              <Grid item xs={12} key={address._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                        <Box sx={{ mr: 2, mt: 0.5 }}>
                          {getAddressIcon(address.type)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ mr: 1 }}>
                              {address.label || address.contactName || 'Address'}
                            </Typography>
                            <Chip
                              label={address.type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {address.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="success"
                                icon={<Star />}
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                          {address.contactPhone && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {address.contactPhone}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {address.street}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {address.city}, {address.state} {address.zipCode}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(address)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAddress(address._id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    {!address.isDefault && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Button
                          size="small"
                          onClick={() => handleSetDefault(address._id)}
                        >
                          Set as Default
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Add Address Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.type === 'home'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.checked ? 'home' : 'work' })}
                    />
                  }
                  label={formData.type === 'home' ? 'Home Address' : 'Work Address'}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="add-state-label">State</InputLabel>
                <Select
                  labelId="add-state-label"
                  value={formData.state}
                  label="State"
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Select a state</em>
                  </MenuItem>
                  {AUSTRALIAN_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Postcode"
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                }
                label="Set as default address"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddAddress}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0A2A66 0%, #1a3a76 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a3a76 0%, #0A2A66 100%)',
              }
            }}
          >
            Add Address
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Address</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.type === 'home'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.checked ? 'home' : 'work' })}
                    />
                  }
                  label={formData.type === 'home' ? 'Home Address' : 'Work Address'}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="edit-state-label">State</InputLabel>
                <Select
                  labelId="edit-state-label"
                  value={formData.state}
                  label="State"
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Select a state</em>
                  </MenuItem>
                  {AUSTRALIAN_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Postcode"
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                }
                label="Set as default address"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEditAddress}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0A2A66 0%, #1a3a76 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a3a76 0%, #0A2A66 100%)',
              }
            }}
          >
            Update Address
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNavigation />
    </Box>
  );
};

export default Addresses;